import { CombatPhaseMachine, DeathWaveMachine, resolveExtraAttackRequest } from '../combat/resolveCombat.js';
import { tickEffectDurations } from '../effects/gc.js';
import { availableEnergy, effectiveCardCost } from '../cards/query.js';
import { isFieldPlan } from './validate.js';
import { planSelections, revalidatePlannedTargets, unitInstanceFromStep } from '../targeting/planning.js';
import { isLiveUnit } from '../registry/subscribers.js';
function compareCommittedPlanOrder(a, b) {
    return a.planIndex - b.planIndex
        || (a.side === b.side ? a.seq - b.seq : a.side === 'A' ? -1 : 1);
}
function playersCanonical(state) {
    return Object.values(state.players).sort((a, b) => {
        if (a.side === b.side)
            return a.id.localeCompare(b.id);
        return a.side === 'A' ? -1 : 1;
    });
}
export class RoundResolutionWorkflow {
    registry;
    growEnergy;
    stage = 'phase';
    /**
     * Единая очередь FIELD, SPELL и VIRUS. Порядок задаёт позиция в committed
     * плане владельца, а при одной позиции — каноническая сторона A → B.
     */
    cards = [];
    fields = [];
    reactions = [];
    /** Текущая валидная FIELD-карта, размещаемая и раскрываемая до следующей карты. */
    placeable = [];
    appearIndex = 0;
    appearOutcome = 'survived';
    /** Transient overlay-receiver, которому placement-транзакция разрешает absorb в руке. */
    materializingReceiver = null;
    planIndex = 0;
    currentSpellFizzled = false;
    extraAttackDeferred = false;
    deathsAfterExtraAttack = false;
    combat = null;
    deathWave = null;
    reservedRevealIds;
    constructor(registry, growEnergy, reservedRevealIds = []) {
        this.registry = registry;
        this.growEnergy = growEnergy;
        this.reservedRevealIds = new Set(reservedRevealIds);
    }
    isCardMoveBlocked(instanceId) {
        return this.reservedRevealIds.has(instanceId);
    }
    isPendingFieldReceiver(instanceId) {
        // Overlay-receiver ещё лежит в руке РОВНО в момент атомарной placement-
        // транзакции (absorb host → занять ячейку); только тогда absorb в руке
        // легален. Дальше он обычный live-юнит на поле.
        return this.materializingReceiver === instanceId;
    }
    currentField() {
        const current = this.cards[this.planIndex];
        if (current === undefined || !isFieldPlan(current.plan))
            throw new Error('round reveal: нет текущей FIELD-карты');
        return current;
    }
    currentSpell() {
        const current = this.cards[this.planIndex];
        if (current === undefined || (current.plan.kind !== 'SPELL' && current.plan.kind !== 'VIRUS'))
            throw new Error('round reveal: нет текущей одноразовой карты');
        return current;
    }
    endMatchIfDefeated(ctx) {
        const players = playersCanonical(ctx.draft);
        const defeated = players.filter((player) => player.health <= 0);
        if (defeated.length === 0)
            return false;
        const winner = defeated.length === players.length
            ? 'draw'
            : players.find((player) => player.health > 0)?.id;
        if (winner === undefined)
            throw new Error('terminal: не удалось определить победителя');
        ctx.emit({ type: 'MatchEnded', winner });
        return true;
    }
    advanceCard() {
        this.planIndex += 1;
        const next = this.cards[this.planIndex];
        if (next === undefined) {
            this.stage = 'combat';
        }
        else {
            this.stage = isFieldPlan(next.plan) ? 'field_reveal' : 'spell_reveal';
        }
    }
    advance(ctx) {
        switch (this.stage) {
            case 'phase':
                ctx.emit({ type: 'PhaseChanged', phase: 'resolution' });
                this.stage = 'prepare_reveal';
                return false;
            case 'prepare_reveal': {
                const all = playersCanonical(ctx.draft)
                    .flatMap((owner) => owner.staged.map((plan, planIndex) => {
                    const instance = ctx.draft.instances[plan.instanceId];
                    if (instance === undefined) {
                        throw new Error(`reveal: экземпляр '${plan.instanceId}' не существует`);
                    }
                    return {
                        plan: structuredClone(plan), planIndex,
                        ownerId: owner.id, side: owner.side, seq: instance.seq,
                    };
                }));
                this.cards = all.filter((entry) => isFieldPlan(entry.plan) || entry.plan.kind === 'SPELL' || entry.plan.kind === 'VIRUS')
                    .sort(compareCommittedPlanOrder);
                this.fields = this.cards.filter((entry) => isFieldPlan(entry.plan));
                this.reactions = all.filter((entry) => entry.plan.kind === 'REACTION').sort((a, b) => {
                    const ap = a.plan;
                    const bp = b.plan;
                    return ap.planSeq - bp.planSeq || (a.side === b.side ? a.seq - b.seq : a.side === 'A' ? -1 : 1);
                });
                this.planIndex = 0;
                // Даже пустой committed-план является точкой истечения временной энергии.
                this.stage = 'pay';
                return false;
            }
            case 'pay': {
                // Один абсолютный ResourcesChanged на игрока: весь committed batch
                // оплачен до первого reveal/hook, а очередь дренируется только после advance.
                for (const owner of playersCanonical(ctx.draft)) {
                    let cost = 0;
                    for (const plan of owner.staged) {
                        const cardCost = effectiveCardCost(ctx.draft, this.registry, plan.instanceId);
                        if (cardCost === undefined)
                            throw new Error(`pay: неизвестная карта '${plan.instanceId}'`);
                        cost += cardCost;
                    }
                    const available = availableEnergy(ctx.draft, owner);
                    if (cost > available)
                        throw new Error(`pay: committed-план игрока '${owner.id}' больше доступной энергии`);
                    const temporary = owner.resources.temporaryEnergyRound === ctx.draft.round
                        ? owner.resources.temporaryEnergy
                        : 0;
                    const spentTemporary = Math.min(cost, temporary);
                    const regularCost = cost - spentTemporary;
                    if (owner.staged.length > 0 || regularCost > 0) {
                        ctx.emit({
                            type: 'ResourcesChanged', playerId: owner.id,
                            energy: owner.resources.energy - regularCost,
                            energyMax: owner.resources.energyMax,
                        });
                    }
                    if (temporary > 0) {
                        ctx.emit({
                            type: 'TemporaryEnergyExpired', playerId: owner.id, amount: temporary,
                            spent: spentTemporary, unused: temporary - spentTemporary, round: ctx.draft.round,
                        });
                    }
                }
                this.planIndex = 0;
                this.stage = this.reactions.length > 0 ? 'reaction_arm'
                    : this.cards.length > 0 && isFieldPlan(this.cards[0].plan) ? 'field_reveal'
                        : this.cards.length > 0 ? 'spell_reveal' : 'combat';
                return false;
            }
            case 'reaction_arm': {
                const current = this.reactions[this.planIndex];
                if (current === undefined || current.plan.kind !== 'REACTION')
                    throw new Error('reaction arm: нет плана');
                const instance = ctx.draft.instances[current.plan.instanceId];
                if (instance === undefined)
                    throw new Error('reaction arm: instance отсутствует');
                const duplicate = ctx.draft.players[current.ownerId].armedReactions
                    .some((id) => ctx.draft.instances[id]?.defId === instance.defId);
                this.reservedRevealIds.delete(instance.id);
                if (instance.zone !== 'hand' || duplicate) {
                    ctx.emit({ type: 'CardFizzled', playerId: current.ownerId, instanceId: instance.id, kind: 'REACTION', reason: duplicate ? 'duplicate_armed' : 'source_unavailable' });
                    if (instance.zone === 'hand')
                        ctx.emit({ type: 'CardMoved', instanceId: instance.id, from: 'hand', to: 'discard' });
                }
                else {
                    ctx.emit({ type: 'CardMoved', instanceId: instance.id, from: 'hand', to: 'armed' });
                    ctx.emit({ type: 'ReactionArmed', playerId: current.ownerId, instanceId: instance.id });
                }
                this.planIndex += 1;
                if (this.planIndex < this.reactions.length)
                    return false;
                this.planIndex = 0;
                this.stage = this.cards.length > 0 && isFieldPlan(this.cards[0].plan) ? 'field_reveal'
                    : this.cards.length > 0 ? 'spell_reveal' : 'combat';
                return false;
            }
            case 'field_reveal': {
                // Авторитетная ревалидация текущей FIELD-карты. В отличие от прежнего
                // batch-first поведения, она полностью разрешается перед следующей
                // картой общей очереди.
                const current = this.currentField();
                this.placeable = [];
                const instance = ctx.draft.instances[current.plan.instanceId];
                if (instance === undefined)
                    throw new Error(`reveal: нет '${current.plan.instanceId}'`);
                ctx.emit({
                    type: 'CardRevealed',
                    playerId: current.ownerId,
                    instanceId: instance.id,
                    kind: 'FIELD',
                    cellId: current.plan.cellId,
                });
                const definition = this.registry.get(instance.defId);
                const revalidated = definition === undefined
                    ? undefined
                    : revalidatePlannedTargets(ctx.draft, this.registry, current.ownerId, definition, current.plan);
                if (revalidated !== undefined)
                    current.plan = revalidated.plan;
                const cell = ctx.draft.board.cells[current.plan.cellId];
                const placement = definition?.planning?.fieldPlacement;
                const overlay = typeof placement === 'object';
                const overlayHostId = overlay
                    ? unitInstanceFromStep(planSelections(current.plan), placement.hostStepId)
                    : undefined;
                const overlayHost = overlayHostId === undefined ? undefined : ctx.draft.instances[overlayHostId];
                const captureReserved = ctx.draft.captureReservations.some((reservation) => reservation.sourceCellId === current.plan.cellId);
                const destinationAvailable = overlay
                    ? overlayHost !== undefined
                        && overlayHost.zone === 'board'
                        && overlayHost.controllerId === current.ownerId
                        && overlayHost.cellId === current.plan.cellId
                        && cell?.occupantId === overlayHost.id
                        && !captureReserved
                    : cell?.occupantId === null && !captureReserved;
                if (instance.zone !== 'hand' || cell === undefined || revalidated?.valid !== true || !destinationAvailable) {
                    this.reservedRevealIds.delete(instance.id);
                    ctx.emit({
                        type: 'CardFizzled', playerId: current.ownerId, instanceId: instance.id, kind: 'FIELD',
                        reason: overlay ? 'overlay_target_invalid' : 'destination_unavailable',
                    });
                    if (instance.zone === 'hand') {
                        ctx.emit({ type: 'CardMoved', instanceId: instance.id, from: 'hand', to: 'discard' });
                    }
                }
                else {
                    this.placeable.push({
                        plan: current.plan, ownerId: current.ownerId, seq: current.seq, defId: instance.defId,
                        overlay, ...(overlayHostId !== undefined ? { hostId: overlayHostId } : {}),
                    });
                }
                this.appearIndex = 0;
                this.stage = 'field_materialize';
                return false;
            }
            case 'field_materialize': {
                // FIELD-карта материально занимает ячейку до своего Появления. Overlay —
                // атомарная разновидность placement: host авторитетно ревалидируется и
                // поглощается (передавая receiver базовую атаку и оставшееся здоровье),
                // receiver занимает ту же ячейку. Поглощение ≠ Гибель (нет UnitDied и death-
                // способностей host). Никаких веток по defId — только generic fieldPlacement.
                for (const entry of this.placeable) {
                    if (entry.overlay) {
                        const host = entry.hostId === undefined ? undefined : ctx.draft.instances[entry.hostId];
                        const receiver = ctx.draft.instances[entry.plan.instanceId];
                        const hostValid = host !== undefined
                            && host.zone === 'board'
                            && host.cellId === entry.plan.cellId
                            && ctx.draft.board.cells[entry.plan.cellId]?.occupantId === host.id;
                        this.reservedRevealIds.delete(entry.plan.instanceId);
                        if (receiver === undefined || receiver.zone !== 'hand' || !hostValid) {
                            entry.fizzled = true;
                            ctx.emit({
                                type: 'CardFizzled', playerId: entry.ownerId, instanceId: entry.plan.instanceId,
                                kind: 'FIELD', reason: 'overlay_target_invalid',
                            });
                            continue;
                        }
                        this.materializingReceiver = entry.plan.instanceId;
                        ctx.ops.absorbCards(entry.plan.instanceId, [host.id]);
                        this.materializingReceiver = null;
                        ctx.emit({ type: 'CardMoved', instanceId: entry.plan.instanceId, from: 'hand', to: 'board' });
                        ctx.emit({ type: 'UnitPlaced', instanceId: entry.plan.instanceId, cellId: entry.plan.cellId, cause: 'played' });
                        continue;
                    }
                    ctx.emit({ type: 'CardMoved', instanceId: entry.plan.instanceId, from: 'hand', to: 'board' });
                    ctx.emit({ type: 'UnitPlaced', instanceId: entry.plan.instanceId, cellId: entry.plan.cellId, cause: 'played' });
                    this.reservedRevealIds.delete(entry.plan.instanceId);
                }
                this.appearIndex = 0;
                this.stage = this.placeable.length > 0 ? 'field_appear_play' : 'field_reveal_completed';
                return false;
            }
            case 'field_appear_play': {
                // Появление текущей FIELD-карты: onPlay сначала, затем UnitEnteredPlay.
                const entry = this.placeable[this.appearIndex];
                if (entry === undefined) {
                    this.stage = 'field_reveal_completed';
                    return false;
                }
                if (entry.fizzled) {
                    // Overlay уже fizzled на этапе placement — без повторного fizzle/Появления.
                    this.appearIndex += 1;
                    this.stage = this.appearIndex < this.placeable.length ? 'field_appear_play' : 'field_reveal_completed';
                    return false;
                }
                this.appearOutcome = 'fizzled';
                const instance = ctx.draft.instances[entry.plan.instanceId];
                if (instance === undefined || !isLiveUnit(ctx.draft, instance)) {
                    // Карта удалена с поля до своего канонического окна Появления ⇒ fizzle.
                    ctx.emit({
                        type: 'CardFizzled', playerId: entry.ownerId, instanceId: entry.plan.instanceId,
                        kind: 'FIELD', reason: 'removed_before_appearance',
                    });
                    this.stage = 'field_appear_completed';
                    return false;
                }
                ctx.scheduleHooks([{
                        subject: { selfId: entry.plan.instanceId },
                        defId: instance.defId,
                        hookName: 'onPlay',
                        cellId: entry.plan.cellId,
                        seq: entry.seq,
                        plan: entry.plan,
                    }]);
                this.stage = 'field_appear_enter';
                return false;
            }
            case 'field_appear_enter': {
                const entry = this.placeable[this.appearIndex];
                if (entry === undefined) {
                    this.stage = 'field_reveal_completed';
                    return false;
                }
                const instance = ctx.draft.instances[entry.plan.instanceId];
                if (instance === undefined || !isLiveUnit(ctx.draft, instance)) {
                    // onPlay сняла карту с поля до её собственного Появления ⇒ fizzle.
                    ctx.emit({
                        type: 'CardFizzled', playerId: entry.ownerId, instanceId: entry.plan.instanceId,
                        kind: 'FIELD', reason: 'removed_before_appearance',
                    });
                    this.stage = 'field_appear_deaths';
                    return false;
                }
                ctx.emit({ type: 'UnitEnteredPlay', instanceId: entry.plan.instanceId, cellId: entry.plan.cellId, cause: 'played' }, entry.plan);
                ctx.emit({ type: 'CardResolved', playerId: entry.ownerId, instanceId: entry.plan.instanceId, kind: 'FIELD' });
                this.appearOutcome = 'survived';
                this.stage = 'field_appear_deaths';
                return false;
            }
            case 'field_appear_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'field_appear_completed';
                }
                return false;
            case 'field_appear_completed': {
                // Публичный lifecycle завершённого Появления: собственная способность и её
                // каскад уже отработали, поэтому «после появления другой карты» триггерится
                // здесь, а не конкурирует с картой в подписчиках UnitEnteredPlay.
                const entry = this.placeable[this.appearIndex];
                if (entry !== undefined) {
                    const instance = ctx.draft.instances[entry.plan.instanceId];
                    const cell = ctx.draft.board.cells[entry.plan.cellId];
                    ctx.emit({
                        type: 'UnitAppeared',
                        instanceId: entry.plan.instanceId,
                        defId: entry.defId,
                        ownerId: entry.ownerId,
                        controllerId: instance?.controllerId ?? entry.ownerId,
                        cellId: entry.plan.cellId,
                        tableId: cell?.tableId ?? 0,
                        cause: 'played',
                        outcome: this.appearOutcome,
                    });
                }
                this.appearIndex += 1;
                this.stage = this.appearIndex < this.placeable.length ? 'field_appear_play' : 'field_reveal_completed';
                return false;
            }
            case 'field_reveal_completed': {
                const hasMoreFields = this.cards.slice(this.planIndex + 1).some((entry) => isFieldPlan(entry.plan));
                if (!hasMoreFields) {
                    const revealedByPlayer = {};
                    const revealedByTable = { '0': [], '1': [], '2': [] };
                    for (const entry of this.fields) {
                        (revealedByPlayer[entry.ownerId] ??= []).push(entry.plan.instanceId);
                        const cell = ctx.draft.board.cells[entry.plan.cellId];
                        if (cell !== undefined)
                            revealedByTable[String(cell.tableId)].push(entry.plan.instanceId);
                    }
                    ctx.emit({ type: 'FieldRevealCompleted', round: ctx.draft.round, revealedByPlayer, revealedByTable });
                }
                this.advanceCard();
                return false;
            }
            case 'spell_reveal': {
                const current = this.currentSpell();
                const instance = ctx.draft.instances[current.plan.instanceId];
                if (instance === undefined)
                    throw new Error(`spell reveal: нет '${current.plan.instanceId}'`);
                ctx.emit({ type: 'CardRevealed', playerId: current.ownerId, instanceId: instance.id, kind: current.plan.kind });
                const definition = this.registry.get(instance.defId);
                const revalidated = definition === undefined
                    ? undefined
                    : revalidatePlannedTargets(ctx.draft, this.registry, current.ownerId, definition, current.plan);
                if (revalidated !== undefined)
                    current.plan = revalidated.plan;
                this.currentSpellFizzled = definition === undefined || instance.zone !== 'hand' || revalidated?.valid !== true;
                if (this.currentSpellFizzled) {
                    const targetKind = current.plan.mode === undefined
                        ? definition?.planning?.target
                        : definition?.planning?.targetByMode?.[current.plan.mode] ?? definition?.planning?.target;
                    const destination = current.plan.targetCellId === undefined
                        ? undefined
                        : ctx.draft.board.cells[current.plan.targetCellId];
                    const reason = targetKind === 'unit+cell' && destination !== undefined && destination.occupantId !== null
                        ? 'destination_unavailable'
                        : 'target_invalid';
                    ctx.emit({ type: 'CardFizzled', playerId: current.ownerId, instanceId: instance.id, kind: current.plan.kind, reason });
                }
                const partial = definition?.planning?.onInvalidAtResolution === 'allow_partial'
                    || definition?.planning?.allowPartialFizzle === true;
                this.stage = !this.currentSpellFizzled || partial
                    ? 'spell_on_play'
                    : 'spell_finish';
                return false;
            }
            case 'spell_on_play': {
                const current = this.currentSpell();
                const instance = ctx.draft.instances[current.plan.instanceId];
                ctx.scheduleHooks([{
                        subject: { selfId: instance.id }, defId: instance.defId, hookName: 'onPlay',
                        seq: current.seq, plan: current.plan,
                    }]);
                this.stage = 'spell_extra_attack';
                return false;
            }
            case 'spell_extra_attack': {
                const request = [...ctx.draft.pendingExtraAttacks].sort((a, b) => a.seq - b.seq)[0];
                if (request === undefined) {
                    this.stage = 'spell_finish';
                    return false;
                }
                ctx.beginEventDeferral();
                this.extraAttackDeferred = true;
                this.deathsAfterExtraAttack = true;
                resolveExtraAttackRequest(ctx, this.registry, request.id, request.attackerId);
                this.stage = 'spell_deaths';
                return false;
            }
            case 'spell_finish': {
                const current = this.currentSpell();
                const instance = ctx.draft.instances[current.plan.instanceId];
                this.reservedRevealIds.delete(current.plan.instanceId);
                if (current.plan.kind === 'VIRUS' && !this.currentSpellFizzled && instance?.zone !== 'attachment') {
                    this.currentSpellFizzled = true;
                    ctx.emit({ type: 'CardFizzled', playerId: current.ownerId, instanceId: current.plan.instanceId, kind: 'VIRUS', reason: 'attachment_not_created' });
                }
                if (instance !== undefined && instance.zone === 'hand') {
                    ctx.emit({ type: 'CardMoved', instanceId: instance.id, from: 'hand', to: 'discard' });
                }
                if (!this.currentSpellFizzled) {
                    ctx.emit({ type: 'CardResolved', playerId: current.ownerId, instanceId: current.plan.instanceId, kind: current.plan.kind });
                }
                this.stage = 'spell_deaths';
                return false;
            }
            case 'spell_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry, this.extraAttackDeferred);
                this.extraAttackDeferred = false;
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    if (this.deathsAfterExtraAttack) {
                        this.deathsAfterExtraAttack = false;
                        this.stage = 'spell_extra_attack';
                    }
                    else
                        this.advanceCard();
                }
                return false;
            case 'combat': {
                this.combat ??= new CombatPhaseMachine(this.registry);
                if (this.combat.advance(ctx))
                    this.stage = 'terminal';
                return false;
            }
            case 'terminal': {
                if (this.endMatchIfDefeated(ctx)) {
                    this.stage = 'done';
                }
                else {
                    this.stage = 'upkeep_draw';
                }
                return false;
            }
            case 'upkeep_draw': {
                for (const player of playersCanonical(ctx.draft)) {
                    // Upkeep completes the current round and immediately opens the next
                    // one below. The fifth completed round therefore starts round 6.
                    ctx.ops.drawCard(player.id, ctx.draft.round >= 5 ? 2 : 1);
                }
                this.stage = 'upkeep_draw_deaths';
                return false;
            }
            case 'upkeep_draw_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'upkeep_energy';
                }
                return false;
            case 'upkeep_energy': {
                for (const player of playersCanonical(ctx.draft)) {
                    const next = this.growEnergy(player.resources);
                    ctx.emit({
                        type: 'ResourcesChanged',
                        playerId: player.id,
                        energy: next.energy,
                        energyMax: next.energyMax,
                    });
                }
                this.stage = 'upkeep_energy_deaths';
                return false;
            }
            case 'upkeep_energy_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'upkeep_duration';
                }
                return false;
            case 'upkeep_duration': {
                for (const tick of tickEffectDurations(ctx.draft)) {
                    if (tick.duration <= 0)
                        ctx.ops.removeEffect(tick.effectId);
                    else {
                        ctx.emit({
                            type: 'EffectDurationChanged',
                            effectId: tick.effectId,
                            duration: tick.duration,
                        });
                    }
                }
                this.stage = 'upkeep_deaths';
                return false;
            }
            case 'upkeep_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'upkeep_timed_destroy';
                }
                return false;
            case 'upkeep_timed_destroy': {
                const due = [...ctx.draft.timedDestroys]
                    .filter((marker) => marker.deadlineRound <= ctx.draft.round)
                    .sort((a, b) => a.seq - b.seq);
                for (const marker of due)
                    ctx.ops.resolveTimedDestroy(marker.id);
                this.stage = 'upkeep_timed_destroy_deaths';
                return false;
            }
            case 'upkeep_timed_destroy_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'round_end';
                }
                return false;
            case 'round_end':
                ctx.emit({ type: 'RoundEnded', round: ctx.draft.round });
                this.stage = 'round_end_deaths';
                return false;
            case 'round_end_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'capture_return';
                }
                return false;
            case 'capture_return': {
                for (const reservation of [...ctx.draft.captureReservations]
                    .filter((candidate) => candidate.expiresRound <= ctx.draft.round)
                    .sort((a, b) => a.seq - b.seq)) {
                    ctx.ops.returnCapture(reservation.captureId, 'expired');
                }
                this.stage = 'capture_return_deaths';
                return false;
            }
            case 'capture_return_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'reset';
                }
                return false;
            case 'reset': {
                for (const player of playersCanonical(ctx.draft)) {
                    for (const plan of [...player.staged]) {
                        if (plan.kind === undefined && isFieldPlan(plan)) {
                            ctx.emit({ type: 'PlacementUnstaged', playerId: player.id, cellId: plan.cellId });
                        }
                        else {
                            ctx.emit({ type: 'CardUnplanned', playerId: player.id, instanceId: plan.instanceId });
                        }
                    }
                    ctx.emit({ type: 'PlayerReadyChanged', playerId: player.id, ready: false });
                }
                ctx.emit({ type: 'PhaseChanged', phase: 'planning' });
                ctx.emit({ type: 'RoundStarted', round: ctx.draft.round + 1 });
                this.stage = 'post_reset_deaths';
                return false;
            }
            case 'post_reset_deaths':
                this.deathWave ??= new DeathWaveMachine(this.registry);
                if (this.deathWave.advance(ctx)) {
                    this.deathWave = null;
                    this.stage = 'post_reset_terminal';
                }
                return false;
            case 'post_reset_terminal':
                this.endMatchIfDefeated(ctx);
                this.stage = 'done';
                return false;
            case 'done':
                return true;
        }
    }
}
//# sourceMappingURL=roundWorkflow.js.map